import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { getCompilations } from '@/store/compilations/actions';
import { getSearch } from '@/store/search/actions';

import { selectCompilations } from '@/store/compilations/selectors';
import { selectSearch } from '@/store/search/selectors';
import { selectUsers } from '@/store/users/selectors';

import {
  AutoComplete,
  Badge,
  Button,
  Col,
  ConfigProvider,
  Divider,
  Flex,
  Input,
  Layout,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import CompilationCard from '@/components/CompilationCard';
import CustomEmpty from '@/components/CustomEmpty';
import { Content } from '@/components/Layout';
import ThemesEditor from '@/components/ThemesEditor';
import { AiOutlineFilter } from 'react-icons/ai';

import CompilationsEmptyPlaceholder from './CompilationsEmptyPlaceholder';

import Sort from '@shared/components/NewSort';
import { SortTypeHelp } from '@shared/components/NewSort/constants';
import useIsMobile from '@shared/hooks/useIsMobile';

import Utils from '@/Utils';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import css from './Compilations.module.scss';

import { COMPILATIONS_TYPE } from '@/constants/topic-types';

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;

const Compilations = ({ isFavorite = false }) => {
  const { t } = useTranslation('compilations');
  const dispatch = useDispatch();
  const [isMobile] = useIsMobile(768);
  const [isTablet] = useIsMobile(993);
  const {
    compilations,
    compilationsPageNumber,
    compilationsTotal,
    compilationsPageSize,
    isLoadingCompilations,
    themes,
  } = useSelector(selectCompilations);
  const { search: searchData } = useSelector(selectSearch);
  const { currentUser } = useSelector(selectUsers);
  const [searchParams, setSearchParams] = useSearchParams();

  const sortTypes = [
    { name: t('rate'), value: ['rate,asc', 'rate,desc'], type: SortTypeHelp.RATE },
    { name: t('date'), value: ['createdDate,asc', 'createdDate,desc'], type: SortTypeHelp.DATE },
  ];

  const [activeSort, setActiveSort] = useState(isFavorite ? 'createdDate,desc' : 'rate,desc');
  const [selectedThemeId, setSelectedThemeId] = useState(+searchParams.get('themeId') || null);
  const [search, setSearch] = useState({
    type: COMPILATIONS_TYPE(t)[0].type,
    value: undefined,
    requestName: COMPILATIONS_TYPE(t)[0].requestName,
  });
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);

  const handlePageChange = page => {
    dispatch(
      getCompilations({
        size: DEFAULT_PAGE_SIZE,
        sort: activeSort,
        themeId: selectedThemeId,
        page: page - 1,
      })
    );
  };

  const getDataDebounce = useCallback(
    debounce(data => {
      dispatch(getCompilations(data));
    }, 500),
    []
  );

  const handleChangeData = (type, value) => {
    let sendData = {
      size: DEFAULT_PAGE_SIZE,
      sort: activeSort,
      themeId: selectedThemeId,
      page: 0,
      [search.requestName]: search.value,
      ...(isFavorite ? { userId: currentUser?.id } : {}),
    };

    switch (type) {
      case 'sort':
        setActiveSort(value);
        break;

      case 'themeId':
        setSelectedThemeId(value);
        break;

      default:
        break;
    }

    getDataDebounce({
      ...sendData,
      [type]: value,
    });
  };

  const getSearchDebounce = useCallback(
    debounce((value, searchType) => {
      dispatch(getSearch(value, searchType, 'compilation', isFavorite));
    }, 500),
    []
  );

  const handleChangeSearch = value => {
    getSearchDebounce(value?.trim(), search.type);
  };

  const handleSearchSubmit = value => {
    setSearch({ ...search, value: value?.trim() });
    handleChangeData(search.requestName, value?.trim());
  };

  const selectSearchOption = option => {
    // TODO: Попросить с бека вернуть типы поиска в id ибо сейчас проверка на НАЗВАНИЕ
    const type =
      COMPILATIONS_TYPE(t).find(item => item?.name.toLowerCase() === option?.type.toLowerCase()) ||
      COMPILATIONS_TYPE(t)[1];

    setSearch({ ...search, value: option?.name, type: type?.type, requestName: type?.requestName });

    handleChangeData(type?.requestName, option?.name);
  };

  // Рендер опций поиска
  const searchOptions = useMemo(
    () =>
      searchData?.map(option => ({
        label: (
          <Flex justify='space-between' align='center'>
            <div>{Utils.trString(option?.name, 80)}</div>
            <div className={css['Compilations-search-type']}>{option?.type}</div>
          </Flex>
        ),
        value: option.name,
        optionData: option,
      })) || [],
    [searchData]
  );

  // Рендер карточек
  const compilationsCards = useMemo(() => {
    if (compilations.length === 0 && !isLoadingCompilations) {
      return <CompilationsEmptyPlaceholder isFavorite={isFavorite} isSearching={!!search.value || !!selectedThemeId} />;
    }

    return compilations.map(compilation => (
      <CompilationCard
        dataqa='userCompilationCard'
        key={compilation.id}
        compilation={compilation}
        isFavorite={isFavorite}
        setSearchParams={id => handleChangeData('themeId', id)}
      />
    ));
  }, [compilations, isLoadingCompilations]);

  // Инициализация
  useEffect(() => {
    dispatch(
      getCompilations({
        size: DEFAULT_PAGE_SIZE,
        sort: activeSort,
        themeId: selectedThemeId,
        ...(isFavorite ? { userId: currentUser?.id } : {}),
      })
    );

    // Очищаем searchParams
    setSearchParams({});
  }, []);

  return (
    <Layout>
      <Content className={css['Compilations']}>
        {/* Header */}
        <Row className={css['Compilations-header']}>
          <Col span={isMobile ? 24 : 8}>
            {!isFavorite && (
              <Title data-qa='compilationsTitle' level={1} className={css['Compilations-title']}>
                {t('compilations')}
              </Title>
            )}
          </Col>

          {/* Search */}
          <Col span={isMobile ? 24 : 16}>
            <Space.Compact size='large' block direction={isTablet ? 'vertical' : 'horizontal'}>
              <ConfigProvider
                theme={{
                  components: {
                    Select: {
                      selectorBg: isMobile ? '#fff' : '#f5f5f5',
                    },
                  },
                }}
              >
                <Select
                  className={css['Compilations-search-select']}
                  value={search.type}
                  options={COMPILATIONS_TYPE(t)}
                  fieldNames={{ label: 'name', value: 'type' }}
                  onChange={(value, { requestName }) => setSearch({ ...search, type: value, requestName })}
                  data-qa='compilationsSearchType'
                />
              </ConfigProvider>
              <AutoComplete
                className={css['Compilations-search']}
                options={searchOptions}
                onSelect={(name, { optionData }) => selectSearchOption(optionData)}
                onSearch={handleChangeSearch}
                data-qa='compilationsAutoComplete'
                defaultValue={search.value}
                backfill
              >
                <Input.Search
                  placeholder={t('search')}
                  enterButton
                  onSearch={handleSearchSubmit}
                  data-qa='compilationsSearch'
                />
              </AutoComplete>
            </Space.Compact>

            {/* Кнопка выбора темы на мобилке */}
            {isMobile && (
              <div className={css['Compilations-theme-mobile']}>
                <Button
                  type='link'
                  className={css['Compilations-theme-mobile-button']}
                  icon={
                    <Badge dot={!!selectedThemeId} color='orange'>
                      <AiOutlineFilter color='var(--personalColor)' size={18} />
                    </Badge>
                  }
                  onClick={() => setIsThemeModalOpen(true)}
                >
                  {t('filterByThemes')}
                </Button>
                <Divider />
                {t('themesFounded', { count: compilationsTotal })}
              </div>
            )}
          </Col>
        </Row>

        {/* Content */}
        <Row wrap>
          {!isMobile && (
            <Col span={8}>
              <ThemesEditor
                isFavorite={isFavorite}
                onlyView
                onSelect={id => handleChangeData('themeId', id)}
                themeId={selectedThemeId}
              />
            </Col>
          )}
          <Col span={isMobile ? 24 : 16}>
            <Flex vertical gap={32}>
              <Sort
                isLoading={isLoadingCompilations}
                handleChangeSort={value => handleChangeData('sort', value)}
                activeSort={activeSort}
                sortTypes={sortTypes}
              />
              <Spin spinning={isLoadingCompilations}>{compilationsCards}</Spin>
              <Flex justify='center' align='center'>
                <Pagination
                  current={compilationsPageNumber + 1}
                  total={compilationsTotal}
                  pageSize={compilationsPageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  responsive
                  hideOnSinglePage
                />
              </Flex>
            </Flex>
          </Col>
        </Row>

        {isMobile && (
          <Modal
            title={t('themesModal')}
            open={isThemeModalOpen}
            onCancel={() => setIsThemeModalOpen(false)}
            footer={null}
          >
            {themes.length > 0 ? (
              <ThemesEditor
                dataqa='compilationsTheme'
                isFavorite={isFavorite}
                onlyView
                onSelect={id => handleChangeData('themeId', id)}
                themeId={selectedThemeId}
              />
            ) : (
              <CustomEmpty />
            )}
          </Modal>
        )}
      </Content>
    </Layout>
  );
};

Compilations.propTypes = {
  isFavorite: PropTypes.bool, // Является ли это страница избранных
};

export default Compilations;
